import * as React from "react"

const MOBILE_BREAKPOINT = 768

/**
 * useIsMobile hook detects if the current viewport is mobile-sized.
 * Uses window.matchMedia to listen for viewport width changes and updates
 * when the breakpoint (768px) is crossed.
 * 
 * @hook
 * @returns {boolean} Whether the viewport is mobile-sized (< 768px)
 * 
 * @example
 * ```tsx
 * const isMobile = useIsMobile();
 * 
 * return (
 *   <div>
 *     {isMobile ? <MobileView /> : <DesktopView />}
 *   </div>
 * );
 * ```
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    mql.addEventListener("change", onChange)
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return !!isMobile
}
